import type { EstadoPublicoTabletop } from '../../dominio/tabletop/atendimento/projecaoPublica'

/** Relógio da ocorrência, enviado à parte para não retransmitir a cena inteira a cada segundo. */
export interface RelogioPublicoTabletop {
  readonly tempoSimuladoSegundos: number
  readonly cronometroRodando: boolean
}

/**
 * Protocolo entre a janela do instrutor e a de projeção (mesma origem, via
 * BroadcastChannel). Só a janela do instrutor envia `ESTADO_PUBLICO` e
 * `RELOGIO`; a projeção envia `SOLICITAR_ESTADO` ao abrir, para não depender
 * de um clique do instrutor para carregar a cena.
 *
 * `RELOGIO` também serve de sinal de vida: é reenviado periodicamente mesmo
 * com o cronômetro parado, e a projeção indica desconexão quando ele some.
 */
export type MensagemProjecaoTabletop =
  | {
      readonly tipo: 'ESTADO_PUBLICO'
      readonly revisao: number
      readonly estado: EstadoPublicoTabletop
      readonly relogio: RelogioPublicoTabletop
    }
  | {
      readonly tipo: 'RELOGIO'
      readonly idSessao: string
      readonly relogio: RelogioPublicoTabletop
    }
  | { readonly tipo: 'SOLICITAR_ESTADO' }

const NOME_CANAL = 'csv_lab_tabletop_projecao_v2'
const TIPOS_CONHECIDOS = new Set(['ESTADO_PUBLICO', 'RELOGIO', 'SOLICITAR_ESTADO'])

/** Intervalo do sinal de vida enviado pelo instrutor. */
export const INTERVALO_SINAL_VIDA_MS = 2000
/** Sem nenhuma mensagem por este tempo, a projeção se considera desconectada. */
export const LIMITE_DESCONEXAO_MS = 5000

export function ehJanelaDeProjecao(): boolean {
  return typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('projecao') === 'tabletop'
}

export class CanalProjecaoTabletop {
  private canal: BroadcastChannel | null = null

  constructor() {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      try {
        this.canal = new BroadcastChannel(NOME_CANAL)
      } catch {
        this.canal = null
      }
    }
  }

  enviar(msg: MensagemProjecaoTabletop) {
    if (!this.canal) return
    try {
      this.canal.postMessage(msg)
    } catch {
      // Ignora erro de serialização se houver
    }
  }

  ouvir(callback: (msg: MensagemProjecaoTabletop) => void): () => void {
    if (!this.canal) return () => {}
    const tratador = (evento: MessageEvent) => {
      if (TIPOS_CONHECIDOS.has(evento.data?.tipo)) {
        callback(evento.data as MensagemProjecaoTabletop)
      }
    }
    this.canal.addEventListener('message', tratador)
    return () => {
      this.canal?.removeEventListener('message', tratador)
    }
  }

  fechar() {
    this.canal?.close()
    this.canal = null
  }
}

export const canalProjecao = new CanalProjecaoTabletop()
